<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white"
  >
    <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
      <div class="flex flex-wrap items-center justify-between">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
            Overview
          </h6>
          <h2 class="text-blueGray-700 text-xl font-semibold">
            活动创建平均耗时
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <el-select v-model="timeRange" @change="handleTimeRangeChange" size="small" style="width: 120px;">
            <el-option label="近7天" :value="7" />
            <el-option label="近14天" :value="14" />
            <el-option label="近30天" :value="30" />
          </el-select>
        </div>
      </div>
    </div>
    <div class="p-4 flex-auto">
      <div class="relative h-350-px">
        <canvas id="avg-time-chart"></canvas>
      </div>
    </div>
  </div>
</template>
<script>
import { Chart, registerables } from "chart.js";
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";

// 注册所有Chart.js组件
Chart.register(...registerables);

// 生成模拟数据
function generateDailyAvgTime(days, baseMinutes, variance) {
  const data = [];
  for (let i = 0; i < days; i++) {
    const value = baseMinutes + (Math.random() - 0.5) * variance;
    data.push(Math.max(0.5, parseFloat(value.toFixed(2))));
  }
  return data;
}

function getDateLabel(daysAgo) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// 从API获取数据
async function fetchAvgTimeStats(days) {
  const token = getUserToken();
  const response = await fetch(getAdminApiUrl('/stat/daily/create/duration'), {
    method: 'POST',
    headers: {
      'Token': `${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ days: days })
  });
  const result = await response.json();
  if (result.sta === 0 && result.data) {
    return result.data;
  }
  return null;
}

export default {
  data() {
    return {
      timeRange: 7,
      chart: null,
    };
  },
  mounted: function () {
    this.loadData();
  },
  methods: {
    async loadData() {
      await this.$nextTick();
      const apiData = await fetchAvgTimeStats(this.timeRange);

      let config;

      if (apiData && apiData.datasets && apiData.datasets.length > 0) {
        config = {
          type: "line",
          data: {
            labels: apiData.labels,
            datasets: apiData.datasets.map(ds => ({
              label: ds.label,
              backgroundColor: ds.color,
              borderColor: ds.color,
              data: ds.data.map(v => Number((v / 60).toFixed(2))), // 秒转分钟，保留2位小数
              fill: false,
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
            }))
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "rgba(0,0,0,.4)" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + Number(context.parsed.y.toFixed(2)) + ' 分钟';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                display: true,
                title: { display: true, text: "日期" },
                grid: { display: false },
              },
              y: {
                display: true,
                title: { display: true, text: "平均耗时 (分钟)" },
                grid: { color: "rgba(33, 37, 41, 0.1)" },
                ticks: {
                  callback: function(value) { return Number(value.toFixed(2)) + ' min'; }
                }
              },
            },
          },
        };
      } else {
        // 模拟数据
        const avgTimeData = generateDailyAvgTime(this.timeRange, 2.5, 1.5);
        const labels = [];
        for (let i = this.timeRange - 1; i >= 0; i--) {
          labels.push(getDateLabel(i));
        }

        config = {
          type: "line",
          data: {
            labels: labels,
            datasets: [
              {
                label: '平均创建耗时',
                backgroundColor: "#8b5cf6",
                borderColor: "#8b5cf6",
                data: avgTimeData.map(v => Number(v.toFixed(2))),
                fill: false,
                tension: 0.3,
                pointRadius: 4,
                pointHoverRadius: 6,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "rgba(0,0,0,.4)" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + Number(context.parsed.y.toFixed(2)) + ' 分钟';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                display: true,
                title: { display: true, text: "日期" },
                grid: { display: false },
              },
              y: {
                display: true,
                title: { display: true, text: "平均耗时 (分钟)" },
                grid: { color: "rgba(33, 37, 41, 0.1)" },
                ticks: {
                  callback: function(value) { return Number(value.toFixed(2)) + ' min'; }
                }
              },
            },
          },
        };
      }

      // 销毁旧图表
      if (window.myAvgTimeChart) {
        window.myAvgTimeChart.destroy();
      }

      var ctx = document.getElementById("avg-time-chart").getContext("2d");
      window.myAvgTimeChart = new Chart(ctx, config);
    },
    handleTimeRangeChange() {
      this.loadData();
    },
  },
};
</script>
